import { useEffect, useState } from "react";
import useIsMobile from "../../../../../hooks/useIsMobile";
import { formatNumberID } from "../../../../../utils/FormatNumber";
import { Chart } from "primereact/chart";
import pattern from "patternomaly";
import { Skeleton } from "primereact/skeleton";
import { AnimatePresence, motion } from "framer-motion";

const OwnerDashboardEmployeeBarChart = (props) => {
  const { data, title, labelKey, valueKey, filterValue, loading } = props;

  const [barChartData, setBarChartData] = useState({});
  const [barChartOptions, setBarChartOptions] = useState({});
  const isMobile = useIsMobile();

  const generateBarChart = () => {
    // if (!data?.current?.length) return;

    const currentData = data.current;
    const previousData = data.previous || [];

    // 1. Sort current data by valueKey (highest to lowest)
    const sorted = [...currentData].sort(
      (a, b) => (b[valueKey] || 0) - (a[valueKey] || 0)
    );

    // 2. Generate aligned previous data
    const alignedPrevious = sorted.map((sortedItem) => {
      const match = previousData.find(
        (p) => p[labelKey] === sortedItem[labelKey]
      );
      return match ? match[valueKey] || 0 : 0;
    });

    // 3. Generate labels (shortened on mobile)
    const getShortLabel = (label) => {
      const nameParts = label.split(" ");
      const first = nameParts[0];
      const second = nameParts[1] || "";
      const thirdInitial = nameParts[2]?.charAt(0) || "";
      return isMobile ? `${first} ${second} ${thirdInitial}`.trim() : label;
    };

    const labels = sorted.map((item) => getShortLabel(item[labelKey]));

    // 4. Build datasets
    const datasets = [];

    datasets.push({
      label: title,
      data: sorted.map((item) => item[valueKey] || 0),
      backgroundColor: "#E69F00",
      borderColor: "#E69F00",
      borderWidth: 1,
    });

    if (previousData.length > 0) {
      datasets.push({
        label: "Previous Period",
        data: alignedPrevious,
        backgroundColor: pattern.draw("diagonal", "#56B4E9"),
        borderColor: "#56B4E9",
        borderWidth: 1,
      });
    }

    // 5. Chart.js options
    const options = {
      indexAxis: isMobile ? "y" : "x",
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "top",
        },
      },
      scales: isMobile
        ? {
            x: {
              beginAtZero: true,
              ticks: {
                callback: (value) => formatNumberID(value),
              },
            },
            y: {
              beginAtZero: true,
            },
          }
        : {
            y: {
              beginAtZero: true,
              ticks: {
                callback: (value) => formatNumberID(value),
              },
            },
            x: {
              beginAtZero: true,
            },
          },
    };

    // 6. Apply chart data
    setBarChartData({ labels, datasets });
    setBarChartOptions(options);
  };

  useEffect(() => {
    if (data) {
      generateBarChart();
    }
  }, [data, isMobile]);

  const ChartSkeleton = ({ title }) => (
    <motion.div
      key="loading"
      className="w-full flex flex-col elevated-card-tight-vertical gap-2 h-[20rem] lg:h-[30rem] border border-gray-300 p-4"
      initial={{ opacity: 0.5 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <Skeleton width="40%" height="1.5rem" className="mb-4" />
      <Skeleton width="100%" height="100%" />
    </motion.div>
  );

  return (
    <AnimatePresence mode="wait">
      {loading ? (
        <ChartSkeleton key="chart-skeleton" title={title} />
      ) : (
        <motion.div
          key="loaded"
          className="w-full flex flex-col elevated-card-tight-vertical gap-2 h-[20rem] lg:h-[30rem] border border-gray-300"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <h2 className="text-lg sm:text-xl font-semibold text-black">
            {title}
          </h2>
          <div className="flex-1 h-full">
            <Chart
              key={isMobile ? "mobile" : "desktop"}
              type="bar"
              data={barChartData}
              options={barChartOptions}
              className="w-full h-full py-4"
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default OwnerDashboardEmployeeBarChart;
